// src/components/EditUserModal.tsx - NEW: Modal for editing user details
import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Loader2, Check, Shield, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAppContext } from '../hooks/useAppContext';
import './shared.css';

interface User {
  api_key: string;
  name: string;
  permissions: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userToEdit: User | null;
  onUserUpdated: () => void;
}

interface FormData {
  name: string;
  permissions: string[];
  is_active: boolean;
}

const AVAILABLE_PERMISSIONS = [
  'search',
  'metadata', 
  'health',
  'export',
  'contacts',
  'suggestions',
  'admin'
];

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  userToEdit,
  onUserUpdated
}) => {
  const { getUser, updateUser } = useApi();
  const { showToast } = useAppContext();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    permissions: [],
    is_active: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user details when modal opens
  useEffect(() => {
    if (isOpen && userToEdit) {
      setIsLoading(true);
      setError(null);
      
      // Fetch full user details
      getUser(userToEdit.api_key)
        .then(userData => {
          if (userData) {
            const typedUserData = userData as User;
            setFormData({
              name: typedUserData.name,
              permissions: typedUserData.permissions || [],
              is_active: typedUserData.is_active
            });
          } else {
            throw new Error('User data not found');
          }
        })
        .catch(err => {
          console.error('Failed to load user details:', err);
          setError('Failed to load user details');
          showToast({
            type: 'error',
            title: 'Loading Error',
            message: 'Failed to load user details'
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, userToEdit, getUser, showToast]);

  // Handle form submission
  const handleSave = useCallback(async () => {
    if (!userToEdit) return;

    if (!formData.name.trim()) {
      showToast({
        type: 'warning',
        title: 'Validation Error',
        message: 'Please enter a user name'
      });
      return;
    }

    if (formData.permissions.length === 0) {
      showToast({
        type: 'warning',
        title: 'Validation Error',
        message: 'Please select at least one permission'
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateUser(userToEdit.api_key, {
        name: formData.name.trim(),
        permissions: formData.permissions,
        is_active: formData.is_active
      });

      showToast({
        type: 'success',
        title: 'User Updated',
        message: `User "${formData.name}" has been updated successfully`
      });

      onUserUpdated();
      onClose();
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: error instanceof Error ? error.message : 'Failed to update user'
      });
    } finally {
      setIsSaving(false);
    }
  }, [userToEdit, formData, updateUser, showToast, onUserUpdated, onClose]);

  // Toggle permission
  const togglePermission = useCallback((permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, onClose]);

  if (!isOpen || !userToEdit) return null;

  return (
    <div className="modal-overlay" onClick={isSaving ? undefined : onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit User</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={isSaving}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading-container">
              <Loader2 size={32} className="loading-spinner" />
              <p>Loading user details...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <AlertCircle size={32} />
              <h3>Error Loading User</h3>
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="info-box">
                <Shield size={16} />
                <span>Editing user: <strong>{userToEdit.name}</strong></span>
              </div>

              <div className="form-group">
                <label htmlFor="edit-name" className="form-label">User Name</label>
                <input
                  id="edit-name"
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter user name..."
                  disabled={isSaving}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Permissions</label>
                <div className="grid grid-3" style={{ gap: '8px', marginTop: '8px' }}>
                  {AVAILABLE_PERMISSIONS.map(permission => (
                    <button
                      key={permission}
                      type="button"
                      className={`btn btn-ghost ${formData.permissions.includes(permission) ? 'btn-primary' : ''}`}
                      onClick={() => togglePermission(permission)}
                      disabled={isSaving}
                      style={{ justifyContent: 'flex-start' }}
                    >
                      <div style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid currentColor', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '8px'
                      }}>
                        {formData.permissions.includes(permission) && <Check size={10} />}
                      </div>
                      <span style={{ textTransform: 'capitalize' }}>{permission}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    disabled={isSaving}
                    style={{ marginRight: '8px' }}
                  />
                  User is active
                </label>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || isLoading || !!error}
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="loading-spinner" />
                Updating...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;